<template>
  <section class="flex flex-col">
    <button
      type="button"
      class="flex w-full items-center gap-2 text-left"
      :class="{ '-my-2 py-2': touchTarget }"
      :aria-expanded="isExpanded"
      :aria-controls="bodyId"
      :data-testid="`${testId}-header`"
      @click="toggle"
    >
      <span class="flex flex-1 items-center font-bold">
        <slot name="title-prefix" />
        {{ title }}
      </span>

      <!-- Rótulo de ação opcional ("Ver…" / "Ocultar…"): o chevron sozinho passa
           despercebido em modais densos; o texto deixa explícito que a seção abre. -->
      <span
        v-if="toggleLabel"
        :data-testid="`${testId}-toggle-label`"
        class="shrink-0 text-sm font-medium text-primary30 whitespace-nowrap"
      >
        {{ toggleLabel }}
      </span>

      <!-- Resumo curto da seção ("pendente", "2", "R$ 1.200"). Usado no Detalhes do
           Plantão em tela cheia (celular), onde as seções nascem recolhidas e o
           resumo evita abrir cada uma para saber o estado. -->
      <span
        v-if="summary"
        :data-testid="`${testId}-summary`"
        class="shrink-0 rounded-full bg-primary5 px-2 py-0.5 text-xs font-medium text-primary30 whitespace-nowrap"
      >
        {{ summary }}
      </span>

      <SVGIcon
        :path="mdiChevronDown"
        class="h-5 w-5 shrink-0 text-primary30 transition-transform duration-200"
        :class="isExpanded ? 'rotate-180' : 'rotate-0'"
      />
    </button>

    <!-- Altura animada por JS, não por `max-h-*`: o conteúdo desta seção (grid de
         check-in/check-out com evidências) é mais alto que qualquer teto fixo, e
         um teto errado cortaria o conteúdo em vez de animar. Mesma técnica do
         `DropdownCard` do design system. -->
    <Transition name="collapse" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
      <div v-if="isExpanded" :id="bodyId" :data-testid="bodyId" class="overflow-hidden">
        <div class="pt-4">
          <slot />
        </div>
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { mdiChevronDown } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'

const props = withDefaults(
  defineProps<{
    title: string
    /** Base dos `data-testid`: gera `{testId}-header` e `{testId}-body`. */
    testId: string
    /**
     * Estado inicial. Semântica DIRETA — `true` nasce aberto.
     *
     * Só é lido no setup: o valor que chega na montagem define o estado inicial
     * e mudanças posteriores não reabrem a seção, para não desfazer o que o
     * usuário acabou de fechar.
     */
    defaultExpanded?: boolean
    /** Texto ao lado do chevron quando a seção está recolhida (ex.: "Ver registros"). */
    expandLabel?: string
    /** Texto ao lado do chevron quando a seção está aberta (ex.: "Ocultar registros"). */
    collapseLabel?: string
    /**
     * Modo CONTROLADO (`v-model:expanded`): quando fornecido, o pai é o dono do
     * estado e `defaultExpanded` é ignorado. Serve para um controle externo
     * (ex.: botão no header do modal) abrir/fechar a seção e refletir o estado.
     * Ausente (`undefined`), a seção governa o próprio estado como sempre.
     */
    expanded?: boolean
    /** Resumo curto ao lado do chevron (ex.: "pendente", "2"). Ausente = nada. */
    summary?: string
    /**
     * Alvo de toque de 40px no cabeçalho (celular, R6): `py-2` soma 16px à área
     * (24px de conteúdo → 40px) e `-my-2` devolve os 16px ao padding do card
     * (`py-3`). A posição visual não muda em nenhuma altura de conteúdo, inclusive
     * com título quebrando linha — um `min-h` pararia de agir e a margem negativa
     * comeria o padding.
     */
    touchTarget?: boolean
  }>(),
  {
    defaultExpanded: true,
    expandLabel: undefined,
    collapseLabel: undefined,
    expanded: undefined,
    summary: undefined,
    touchTarget: false
  }
)

const emit = defineEmits<{
  'update:expanded': [value: boolean]
}>()

const internalExpanded = ref(props.defaultExpanded)

const isControlled = computed(() => props.expanded !== undefined)

const isExpanded = computed(() =>
  isControlled.value ? (props.expanded as boolean) : internalExpanded.value
)

const toggleLabel = computed(() => (isExpanded.value ? props.collapseLabel : props.expandLabel))

const bodyId = `${props.testId}-body`

const toggle = () => {
  const next = !isExpanded.value
  if (!isControlled.value) internalExpanded.value = next
  emit('update:expanded', next)
}

// Mede a altura real, anima de 0 até ela e devolve `auto` no fim — sem `auto` o
// conteúdo ficaria preso na altura medida e não acompanharia mudanças internas.
const onEnter = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = 'auto'
  const { height } = getComputedStyle(el)
  el.style.height = '0'
  getComputedStyle(el)
  requestAnimationFrame(() => {
    el.style.height = height
  })
}

const onAfterEnter = (element: Element) => {
  ;(element as HTMLElement).style.height = 'auto'
}

const onLeave = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = getComputedStyle(el).height
  getComputedStyle(el)
  requestAnimationFrame(() => {
    el.style.height = '0'
  })
}
</script>

<style scoped>
.collapse-enter-active,
.collapse-leave-active {
  transition: height 0.3s ease-in-out;
  overflow: hidden;
}
</style>
